import styled, { css } from 'styled-components';
import { COLORS } from '@r1-frontend/ui-react/assets/js/constants';
import GridContainer from '@r1-frontend/ui-react/components/layouts/gridContainer';
import { FlexBoxCol, FlexBoxRow } from '@r1-frontend/ui-react/components/layouts/flexBoxGrid';
import { fonts } from '~/src/helpers/font';
import { breakPoints,
    styleWithBreakPoints,
    CSSPropWithBreakpointsGeneric,
    CSSPropWithBreakpoints,
    blockMargin } from '~/src/components/LandingBlock/styled';
import { Button } from '~/src/components/LandingBlock/Header/CallbackForm/styled';

export interface IHeaderBlockStyles {
    [key: string]: CSSPropWithBreakpoints;
}

export const Grid = styled(GridContainer)`
  display: flex;
  flex-direction: column;
`;
export const Col = styled(FlexBoxCol)``;

export const Row = styled(FlexBoxRow)``;

export const RowGrow = styled(FlexBoxRow)<{direction?: string}>`
  flex: 1 1 auto !important;
  order: 1;
  flex-direction: ${({ direction }) => direction === 'vertical' ? 'column' : 'row'};
`;

export const Wrapper = styled.header<{ [key: string]: string }>`
  position: relative;
  width: 100%;
  height: ${p => p.isAutoHeightSize ? 'auto' : '100vh'};
  min-height: 420px;
  background: ${p => p.background ? `url(${p.background}) no-repeat center / cover` : 'transparent'};
  display: flex;
  margin-top: ${p => p.marginTop ? `${p.marginTop}px` : 0};
  margin-bottom: ${p => p.marginBottom ? `${p.marginBottom}px` : 0};
  
  @media (max-width: ${breakPoints.tablet}) {
    min-height: 300px;
    height: auto;
  }
  
  ${p => blockMargin(p)}; 
  ${p => styleWithBreakPoints(p, 'Header')};
`;

export const SubTitle = styled.div`
  margin: 0;
  white-space: pre-wrap;
  max-width: 500px;
  font: var(--bold-h4);
  
  @media (max-width: ${breakPoints.tablet}) {
    max-width: 100%;
    font: var(--bold-h4);
  }
  
  ${p => styleWithBreakPoints(p, 'Header.SubTitle')};
`;

export const Title = styled.h1`
  font: ${fonts.H1};
  margin: 8px 0 0;
  color: ${COLORS.black};
  white-space: pre-wrap;
  max-width: 500px;

  @media (max-width: ${breakPoints.sDesktop}) {
    font: var(--bold-h1);
  }

  @media (max-width: ${breakPoints.tablet}) {
    max-width: 100%;
    font: var(--bold-h3);
  }

  ${p => styleWithBreakPoints(p, 'Header.Title')};
`;

export const Text = styled.div`
  font: ${fonts.L};
  margin: 12px 0 0;
  color: ${COLORS.black};

  @media (max-width: ${breakPoints.tablet}) {
    max-width: 100%;
    white-space: normal;
  }

  ${p => styleWithBreakPoints(p, 'Header.Text')};
`;

export const BtnWrap = styled.div`
  margin-top: 32px;

  @media (max-width: ${breakPoints.mobile}) {
    margin-top: 20px;
    margin-bottom: 10px
  }
`;

export const Image = styled.div<{direction?: string}>`
    object-fit: contain;
    ${({ direction }) => direction === 'horizontal'
    ? css`
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 13.5%;
      z-index: 1;
      max-width: 700px;
      overflow: hidden;
    ` : css`
        position: relative;
        display: flex;
        justify-content: center;
    `}

  img {
    ${p => p.direction === 'vertical' ? styleWithBreakPoints(p, 'Header.Image')
    : css`
        height: 100%;
        width: 100%;
    `};
    min-width: 130px;
    
    @media (max-width: ${breakPoints.mobile}) {
        max-width: 450px;
    }
  }

  @media (max-width: 535px) {
    margin-top: 30px;
  }
  @media (max-width: 340px) {
    margin-top: 40px;
  }
  
  @media (max-width: ${breakPoints.mobile}) {
    width: 100%;
    text-align: center;
  }
  ${p => getImageOffset(p)};
  ${p => getImageParams(p)};
  ${p => p.direction === 'horizontal' ? styleWithBreakPoints(p, 'Header.Image') : ''}
`;

const topStyle = `
    position: inherit;
    transform: none;
    overflow: visible;
    width: 100%;
    order: 0;
`;

const bottomStyle = `
    position: inherit;
    transform: none;
    overflow: visible;
    width: 100%;
    order: 3;
`;

const IMAGE_PLACEMENT_VALUE = {
    top: topStyle,
    bottom: bottomStyle,
};

interface ImagePlacement {
    imagePlacement: CSSPropWithBreakpointsGeneric<'top'|'bottom'> | string | undefined
}

const getImageParams = ({ imagePlacement }: any & ImagePlacement) => {
    if (!imagePlacement) {
        return '';
    }
    const { desktop, mobile, tablet, sDesktop } = imagePlacement as CSSPropWithBreakpointsGeneric<'top'|'bottom'>;

    return css`
        ${desktop
        ? `@media (max-width: ${breakPoints.desktop}) {
                ${(IMAGE_PLACEMENT_VALUE[desktop] || '')}
               }`
        : ''};
        ${sDesktop
            ? `@media (max-width: ${breakPoints.sDesktop}) {
                ${(IMAGE_PLACEMENT_VALUE[sDesktop] || '')}
               }`
        : ''};
        ${tablet
        ? `@media (max-width: ${breakPoints.tablet}) {
                ${(IMAGE_PLACEMENT_VALUE[tablet] || '')}
               }`
        : ''};
        ${mobile
        ? `@media (max-width: ${breakPoints.mobile}) {
                ${(IMAGE_PLACEMENT_VALUE[mobile] || '')}
               }`
        : ''};                
        `;
};

export const getImageOffset = (p: any) => {
    return css`
        top: ${p['offsetY']?.desktop ?? ''};
        right: ${p['offsetX']?.desktop ?? ''};
    
        @media (max-width: ${breakPoints.sDesktop}) {
            top: ${p['offsetY']?.sDesktop ?? ''};
            right: ${p['offsetX']?.sDesktop ?? ''};
        }
        
        @media (max-width: ${breakPoints.tablet}) {
            top: ${p['offsetY']?.tablet ?? ''};
            right: ${p['offsetX']?.tablet ?? ''};
        }
        
        @media (max-width: ${breakPoints.mobile}) {
            top: ${p['offsetY']?.mobile ?? ''};
            right: ${p['offsetX']?.mobile ?? ''};
        }
    `;
};

export const StickyForm = styled.div`
    padding: 8px 0;
    display: none;
    
     ${Button}{
         opacity: 1;
     }  
     
     @media (max-width: ${breakPoints.mobile}){
          display: block;
     }
`;

export const Blur = styled.div`
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    right: 0;
    z-index: -1;
    overflow: hidden;
`;
